import { useLocation } from "wouter";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeleteInsured } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { formatCurrencyValue } from "@/lib/policy-activity-formatter";
import type { Insured, Policy } from "@shared/schema";

// CSR-1970 — confirmation for the insured Delete action, opened from inside
// EditInsuredDialog (mirroring EditPolicyDialog, where Delete also sits in the
// edit surface rather than on the profile itself).
//
// The policy list is the point of this dialog, not decoration: deleting an
// insured ALSO deletes its policies (the Data Center soft-deletes them in the
// same transaction, because an insured hidden while its policies stay live is
// re-linked and resurrected by the next carrier sweep). CS agents reach for this
// on empty "ghost" duplicates, but the real duplicates in the wild are not
// always empty — the ticket's own example (dc-5099) carries a live Progressive
// policy — so every policy about to disappear is named, with its premium, before
// the user can commit.

interface DeleteInsuredDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insured: Insured;
  policies: Policy[];
  /**
   * Whether the policy list is KNOWN, as opposed to merely empty. See the note
   * on the same prop in InsuredSidebar. Defaults to true so existing callers and
   * tests that pass a real list behave unchanged.
   */
  policiesLoaded?: boolean;
  /**
   * CSR-1970 — fired after a successful delete, before navigating away. Lets the
   * surface this is nested inside (the Edit dialog) close itself, so the profile
   * that is about to 404 isn't left sitting open behind it.
   */
  onDeleted?: () => void;
}

export function DeleteInsuredDialog({
  open,
  onOpenChange,
  insured,
  policies,
  policiesLoaded = true,
  onDeleted,
}: DeleteInsuredDialogProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const deleteMutation = useDeleteInsured();

  const policyCount = policies.length;
  // Fail CLOSED. An empty list is only trustworthy if we know it loaded: the
  // page turns a failed policy fetch into `[]`, which would otherwise render a
  // reassuring "no policies affected" dialog while DC deletes live policies the
  // user was never shown. Blocking is the right default because the action is
  // irreversible and the warning is the entire point of this dialog.
  const policiesUnknown = !policiesLoaded;

  const handleConfirm = () => {
    // Belt and braces: the button is disabled in this state, but an irreversible
    // action should never depend on a disabled attribute alone.
    if (policiesUnknown) return;
    deleteMutation.mutate(insured.id as string, {
      onSuccess: () => {
        toast({
          title: "Insured deleted",
          description:
            policyCount > 0
              ? `${insured.name ?? "The insured"} and ${policyCount} ${
                  policyCount === 1 ? "policy" : "policies"
                } were removed.`
              : `${insured.name ?? "The insured"} was removed.`,
        });
        onOpenChange(false);
        onDeleted?.();
        // Leave the profile — it 404s from here on.
        navigate("/insureds");
      },
      onError: (err: Error) => {
        toast({
          title: "Failed to delete insured",
          description: err.message,
          variant: "destructive",
        });
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="dialog-delete-insured">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this insured?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                <span className="font-semibold">{insured.name ?? "This insured"}</span>{" "}
                will be removed from search, lists and reporting.{" "}
                <span className="font-semibold">This action cannot be undone.</span>
              </p>

              {policiesUnknown && (
                <div
                  className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
                  data-testid="delete-insured-policies-unknown"
                >
                  <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    This insured&rsquo;s policies could not be loaded
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Deleting also deletes every policy on this insured, and we
                    can&rsquo;t show you which ones those are right now. Close
                    this, reload the page, and try again.
                  </p>
                </div>
              )}

              {!policiesUnknown && policyCount > 0 && (
                <div
                  className="rounded-md border border-destructive/40 bg-destructive/5 p-3"
                  data-testid="delete-insured-policy-warning"
                >
                  <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {policyCount === 1
                      ? "1 policy will also be deleted"
                      : `${policyCount} policies will also be deleted`}
                  </p>
                  <ul
                    className="mt-2 space-y-1 text-sm"
                    data-testid="delete-insured-policy-list"
                  >
                    {policies.map((policy) => (
                      <li
                        key={policy.id as string}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <span className="truncate">
                          <span className="font-medium">
                            {policy.policyNumber ?? "--"}
                          </span>
                          <span className="text-muted-foreground">
                            {" "}
                            · {policy.carrier ?? "--"}
                          </span>
                        </span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {formatCurrencyValue(policy.premium)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deleteMutation.isPending}
            className="cursor-pointer"
            data-testid="button-cancel-delete-insured"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-2 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={deleteMutation.isPending || policiesUnknown}
            data-testid="button-confirm-delete-insured"
          >
            {deleteMutation.isPending && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {policiesUnknown
              ? "Delete insured"
              : policyCount > 0
                ? `Delete insured and ${policyCount === 1 ? "policy" : `${policyCount} policies`}`
                : "Delete insured"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
